import type { ReactNode } from 'react';
import { Avatar } from '@up2d8/ui';
import { deskIcons } from './desk-sidebar';

/**
 * De kopbalk van de medewerker op een bureauscherm (replica-spec §5).
 *
 * Alle maten komen letterlijk uit `docs/design/UP2D8-home-referentie.html`: hoogte 68, padding
 * `0 28px`, zoekveld hoogte 40 met 42px linkerruimte voor het icoon, twee ronde knoppen van 40, een
 * scheidingslijn van 1×24, en de profielchip.
 *
 * Eén verschil met de mockup, en het is er een uit noodzaak: de knoppen hier dragen **echte**
 * tellers. Toont de mockup een 2 en een 5, dan staat daar bij ons wat er werkelijk open staat, of
 * niets. Een badge die altijd hetzelfde getal toont, leert een medewerker hem te negeren.
 */

export interface DeskHeaderProps {
  /** Het zoekveld; de app levert de bestaande `GlobalSearch` aan. */
  search: ReactNode;
  /** Ongelezen gesprekken, of `null` wanneer `messaging` uit staat. */
  messageCount?: number | null;
  /** Ongelezen meldingen. */
  notificationCount?: number | null;
  userName: string;
  /** "Productie · Ploeg B" in de mockup; leeg als we de ploeg niet kennen. */
  userRole?: string | null;
  onOpenMessages?: () => void;
  onOpenNotifications?: () => void;
  onOpenProfile?: () => void;
}

function IconButton({
  label,
  count,
  onClick,
  children,
}: {
  label: string;
  count?: number | null;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={count ? `${label} (${count})` : label}
      onClick={onClick}
      className="relative flex h-10 w-10 items-center justify-center rounded-pill text-text-secondary transition-colors hover:bg-background hover:text-brand-primary"
    >
      {children}
      {count ? (
        // De witte rand van 2px hoort erbij: zonder die rand loopt de badge visueel in het icoon.
        <span className="absolute right-0.5 top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-pill border-2 border-surface bg-brand-orange px-[5px] font-heading text-micro font-semibold text-text-on-dark">
          {count}
        </span>
      ) : null}
    </button>
  );
}

export function DeskHeader({
  search,
  messageCount,
  notificationCount,
  userName,
  userRole,
  onOpenMessages,
  onOpenNotifications,
  onOpenProfile,
}: DeskHeaderProps) {
  return (
    <header
      data-testid="desk-header"
      className="sticky top-0 z-50 hidden h-[68px] items-center gap-5 border-b border-border-light bg-surface px-7 desk:flex"
    >
      <div className="relative max-w-[480px] flex-1">{search}</div>

      <div className="ml-auto flex items-center gap-2">
        <IconButton label="Gesprekken" count={messageCount} onClick={onOpenMessages}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </IconButton>

        <IconButton label="Meldingen" count={notificationCount} onClick={onOpenNotifications}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </IconButton>

        <span aria-hidden className="mx-1 h-6 w-px bg-border" />

        <button
          type="button"
          onClick={onOpenProfile}
          className="flex items-center gap-2.5 rounded-pill py-1 pl-1 pr-3 transition-colors hover:bg-background"
        >
          <Avatar name={userName} self size="md" />
          <span className="text-left leading-[1.2]">
            <span className="block font-heading text-body font-semibold text-text-primary">
              {userName}
            </span>
            {userRole ? (
              <span className="block text-caption text-text-muted">{userRole}</span>
            ) : null}
          </span>
          <span aria-hidden className="text-text-muted">
            {deskIcons.chevronDown}
          </span>
        </button>
      </div>
    </header>
  );
}
